import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FEATURE_FLAGS, ENV } from '../config/featureFlags';
import { createNitroAd } from './nitroPay';
import './NitroAd.css';

/**
 * Self-contained NitroPay ad placement. Drop it in with no config:
 *
 *     <NitroAd />                       // horizontal banner, same treatment everywhere
 *     <NitroAd variant="rail" />        // a different preset, when we add one
 *
 * It owns everything — a unique placement id (auto-generated), container,
 * centering, responsive reserved height, accepted sizes, and (on localhost) a
 * labeled dev placeholder — so callers pass nothing. Each `variant` is a preset
 * in PLACEMENTS below. Props exist only as escape hatches (e.g. a stable `id`
 * for NitroPay reporting).
 *
 * Responsive sizing: a variant declares one or more breakpoint `slots`, each
 * with its own NitroPay `mediaQuery` + `sizes`. We create a separate NitroPay
 * placement per slot; NitroPay only fills the one whose mediaQuery matches the
 * viewport, and CSS shows only that slot. This is what keeps a desktop
 * leaderboard (728/970×90) from ever being served — and overflowing — on a
 * phone: mobile only requests 320×50/320×100.
 *
 * Ad-free for supporters: any logged-in user with an active Patreon subscription
 * (any tier) gets no ads anywhere — nothing renders and createAd never fires.
 * Keyed off the same signal Settings uses to unlock perks.
 *
 * Behavior by env (all gated by FEATURE_FLAGS.ads — renders nothing when off):
 *   - localhost: real NitroPay fill doesn't work (domain isn't registered), so
 *     render a labeled placeholder per slot; only the matching breakpoint shows.
 *   - staging/prod: render the real per-slot containers and call createAd.
 *     NitroPay adds its own AdChoices/report UI.
 *
 * createAd runs once per mount. Unmounting removes the slot element, which is
 * NitroPay's documented cleanup. Real route changes are signaled from AppBody
 * via `notifyNitroPayNavigation()` (pathname only — hash writes from deep-link
 * tabs/filters must NOT refresh ads; see nitroPay.js).
 *
 * Sticky: the placement is `position: sticky` and pins to the top of the
 * viewport once you scroll past it, staying until the page scrolls on. `stickyTop`
 * is the offset from the top so the ad clears whatever bar is pinned above it.
 * Default `'auto'` measures the site's sticky top bar (`.stickyBreadcrumbBar`,
 * which is taller on pages with an action bar) and re-measures on resize, so the
 * ad sits just below it on every page. Pass a number/CSS length to override, or
 * `0` to pin flush to the top.
 *
 * @param {string}   [id]      stable placement id (DOM id + NitroPay key); auto-generated if omitted.
 * @param {string}   [variant] preset name from PLACEMENTS (default 'horizontal').
 * @param {string|number} ['auto'] [stickyTop] offset from viewport top while pinned. Default 'auto' (measure the sticky top bar).
 * @param {string}   [className] override the preset's wrapper class.
 * @param {object}   [style]   inline style for the wrapper.
 */

// Each placement preset declares breakpoint slots. A slot is one NitroPay
// placement scoped to a mediaQuery, with the sizes appropriate for that width.
// The matching slot's container class (in NitroAd.css) reserves height and
// hides the non-matching breakpoint. Mobile deliberately omits the wide
// leaderboard sizes so an oversized creative can't be served on a phone.
const PLACEMENTS = {
    horizontal: {
        className: 'nitroAd nitroAd--horizontal',
        slots: [
            {
                key: 'desktop',
                mediaQuery: '(min-width: 768px)',
                // NitroPay sizes are NUMERIC [w, h] pairs (per their API/examples).
                // Passing strings makes NitroPay ignore them and serve a default
                // size — which is why a 300×250 showed on mobile and the wrong size
                // on desktop before this was fixed.
                sizes: [
                    [970, 90],
                    [728, 90],
                    [468, 60],
                ],
                className: 'nitroAdSlot nitroAdSlot--desktop',
                label: '728×90 / 970×90',
                // Reserved slot height — MUST match .nitroAdSlot--desktop min-height
                // in NitroAd.css. Used as a FIXED offset for the sticky-stack var
                // so it never reacts to the live ad's height (see coordination effect).
                reservedHeight: 90,
            },
            {
                key: 'mobile',
                mediaQuery: '(max-width: 767px)',
                // Only the short 320×50 mobile leaderboard — no taller rectangle.
                sizes: [[320, 50]],
                className: 'nitroAdSlot nitroAdSlot--mobile',
                label: '320×50',
                // Reserved slot height — MUST match .nitroAdSlot--mobile min-height.
                reservedHeight: 50,
            },
        ],
    },
};

// Auto-incrementing suffix so each <NitroAd /> gets a unique DOM/NitroPay id
// without the caller passing one.
let placementSeq = 0;

// Height of the site's sticky top bar, EXCLUDING its decorative bottom fade
// (`.stickyBreadcrumbBarBottom`, a ~25px transparent gradient). The fade is part
// of the bar's box but not its visible solid area, so counting it would pin the
// ad ~25px too low. Returns the solid height the ad should sit beneath.
function measureTopBarHeight() {
    if (typeof document === 'undefined') return 0;
    const bar = document.querySelector('.stickyBreadcrumbBar');
    if (!bar) return 0;
    let h = bar.getBoundingClientRect().height;
    const fade = bar.querySelector('.stickyBreadcrumbBarBottom');
    if (fade) h -= fade.getBoundingClientRect().height;
    return Math.round(Math.max(0, h));
}

export function NitroAd({ id, variant = 'horizontal', stickyTop = 'auto', className, style }) {
    const preset = PLACEMENTS[variant] || PLACEMENTS.horizontal;

    // Resolve the sticky offset. 'auto' (default) measures the site's sticky top
    // bar so the ad pins just below it on every page; the bar is taller where an
    // action bar is present (deck/author/user details). A ResizeObserver keeps it
    // correct across breakpoints and async content. An explicit number/length
    // overrides the measurement.
    const autoMeasure = stickyTop === 'auto';
    const [measuredTop, setMeasuredTop] = useState(0);
    useEffect(() => {
        if (!autoMeasure || typeof document === 'undefined') {
            return undefined;
        }
        const measure = () => setMeasuredTop(measureTopBarHeight());
        measure();
        // Re-measure on viewport changes and after layout settles (async page
        // content). Deliberately NOT a ResizeObserver on the bar: on the
        // leaderboard an import button toggles inside the bar on scroll, changing
        // its height — observing that would rewrite the ad's sticky `top`
        // mid-scroll and cause a scroll "pop". The bar's solid height is stable
        // per breakpoint, so mount + resize + a couple settle ticks is enough.
        const t1 = setTimeout(measure, 250);
        const t2 = setTimeout(measure, 1000);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            window.removeEventListener('resize', measure);
        };
    }, [autoMeasure]);

    let stickyTopValue;
    if (autoMeasure) {
        stickyTopValue = `${measuredTop}px`;
    } else if (typeof stickyTop === 'number') {
        stickyTopValue = `${stickyTop}px`;
    } else {
        stickyTopValue = stickyTop;
    }

    // Assign a stable base id once per instance: caller-provided, else generated.
    // Each breakpoint slot gets `${baseId}-${slot.key}`.
    const idRef = useRef(null);
    if (idRef.current === null) {
        idRef.current = id || `nitroad-${variant}-${(placementSeq += 1)}`;
    }
    const baseId = idRef.current;

    // Supporters (active Patreon subscription, any tier) are ad-free. Same
    // signal Settings uses to unlock perks. Anonymous/non-subscribers → false.
    const isAdFreeUser = useSelector((state) => {
        const sf = state?.profile?.preferences?.subscriberFeatures;
        return Boolean(sf?.isSubscribed) || parseInt(sf?.subscriberTier, 10) > 0;
    });

    const adsShown = FEATURE_FLAGS.ads && !isAdFreeUser;

    // Guards against React 18 StrictMode's double-invoke in dev so createAd
    // fires exactly once per real mount.
    const created = useRef(false);

    useEffect(() => {
        // Local dev shows a placeholder instead of a real ad, so nothing to create.
        if (ENV.isLocal) return;
        // Supporters get no ad — don't create one (handles the case where their
        // profile resolves after mount: the slot also unmounts via the guard below).
        if (isAdFreeUser) return;
        if (created.current) return;
        created.current = true;
        preset.slots.forEach((slot) => {
            createNitroAd(`${baseId}-${slot.key}`, {
                format: 'display',
                sizes: slot.sizes,
                mediaQuery: slot.mediaQuery,
            });
        });
    }, [isAdFreeUser]);

    // Sticky-stack coordination. The ad pins below the top breadcrumb/action bar;
    // other sticky bars further down the page (results toolbar, deck tab/side bar,
    // meta tab bar) must in turn sit BELOW the ad. We publish the ad's pinned
    // bottom edge as a global CSS var `--cs-secondary-sticky-top` that those bars
    // add to their own `top` (falling back to their normal offset when the var is
    // absent — i.e. ads off / supporter, so nothing regresses). Cleared on unmount.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const root = document.documentElement;
        if (!adsShown) {
            root.style.removeProperty('--cs-secondary-sticky-top');
            return undefined;
        }
        const compute = () => {
            const adTop = autoMeasure
                ? measuredTop
                : typeof stickyTop === 'number'
                ? stickyTop
                : parseFloat(stickyTop) || 0;
            // Use the RESERVED slot height for the active breakpoint — a FIXED
            // value, NOT the live ad's height. The real ad fills and then
            // refreshes (~every 30s) at varying heights; measuring/observing it
            // would rewrite this var mid-scroll and shift the sticky toolbar
            // below it (the scroll "pop"). The reserved height is what the slot
            // occupies, so the toolbar parks just under the ad and never moves.
            const active = preset.slots.find((s) => window.matchMedia(s.mediaQuery).matches) || preset.slots[0];
            const adH = active.reservedHeight || 0;
            root.style.setProperty('--cs-secondary-sticky-top', `${Math.round(adTop + adH)}px`);
        };
        compute();
        // Recompute only on viewport changes (breakpoint flip → different reserved
        // height) and when the bar measurement settles — never off the live ad.
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('resize', compute);
            root.style.removeProperty('--cs-secondary-sticky-top');
        };
    }, [adsShown, autoMeasure, stickyTop, measuredTop, preset]);

    if (!FEATURE_FLAGS.ads || isAdFreeUser) {
        return null;
    }

    const wrapperClassName = className || preset.className;
    const wrapperStyle = { '--nitro-sticky-top': stickyTopValue, ...style };

    return (
        <div className={wrapperClassName} style={wrapperStyle}>
            {preset.slots.map((slot) => (
                <div key={slot.key} className={slot.className}>
                    {/* TEMPORARILY DISABLED: the Patreon upsell underlay. It sat
                        BEHIND the ad (z-index 0) and only showed while loading or
                        when the slot never filled. Commented out (not deleted)
                        while diagnosing NitroPay fill — an absolutely-positioned
                        element overlapping the ad bounds may be flagged by
                        NitroPay's Scalibur ad-quality scan. Re-enable once the
                        prod domain is approved/serving. */}
                    {/* <a
                        className="nitroAdSupportLink"
                        href="https://www.patreon.com/user?u=4796225"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Support us on Patreon to remove ads &amp; unlock more →
                    </a> */}
                    {ENV.isLocal ? (
                        <div className="nitroAdPlaceholder" aria-hidden="true">
                            <span className="nitroAdPlaceholderTag">Advertisement</span>
                            <span className="nitroAdPlaceholderMeta">
                                NitroPay · {baseId}-{slot.key} · {slot.label}
                            </span>
                        </div>
                    ) : (
                        <div id={`${baseId}-${slot.key}`} className="nitroAdFill" />
                    )}
                </div>
            ))}
        </div>
    );
}
